import HttpClient from '../../adapter/HttpClient';
import AcctAccount from '../../entity/AcctAccount';
import BackgroundChargeJob from '../../entity/BackgroundChargeJob';
import BankAccount from '../../entity/BankAccount';
import BillUser from '../../entity/BillUser';
import PaymentDetail from '../../entity/PaymentDetail';
import PaymentExtProvider from '../../entity/PaymentExtProvider';
import PaymentHistory from '../../entity/PaymentHistory';
import ProductPricing from '../../entity/ProductPricing';
import UserPayee from '../../entity/UserPayee';
import ZenginBank from '../../entity/ZenginBank';

class AcctWebApi {
  private httpClient: HttpClient;

  constructor({ httpClient }: { httpClient: HttpClient }) {
    this.httpClient = httpClient;
  }

  public getBillUser = async (kaizenUserId: string): Promise<BillUser> => {
    const ret = await this.httpClient.get(`/acct/kaizen_users/${kaizenUserId}/bill_user`);
    return BillUser.fromJSON(ret.data.data);
  };

  public getAccount = async (organizationId: string): Promise<AcctAccount> => {
    const ret = await this.httpClient.get(`/acct/account?organization_id=${organizationId}`);
    return AcctAccount.fromJSON(ret.data.data);
  };

  public createCard = async (formData: FormData): Promise<void> => {
    await this.httpClient.post('/acct/credit_cards', formData);
  };

  public getProductPricing = async (kaizenUserId: string): Promise<ProductPricing> => {
    const ret = await this.httpClient.get(`/acct/applications/point_product_pricing?kaizen_user_id=${kaizenUserId}`);
    return ProductPricing.fromJSON(ret.data.data);
  };

  public chargePoint = async (formData: FormData): Promise<string> => {
    const ret = await this.httpClient.post('/acct/applications/points', formData);
    return ret.data.data.job_id;
  };

  public getBackgroundChargeJob = async (jobId: string): Promise<BackgroundChargeJob> => {
    const ret = await this.httpClient.get(`/acct/background_jobs/${jobId}`);
    return BackgroundChargeJob.fromJSON(ret.data.data);
  };

  public getZenginBank = async (bankCode: string, branchCode?: string): Promise<ZenginBank> => {
    const ret = await this.httpClient.get(`/acct/zengin_code?bank_code=${bankCode}&branch_code=${branchCode}`);
    return ZenginBank.fromJSON(ret.data);
  };

  public getUserPayee = async (agentId: string): Promise<UserPayee | undefined> => {
    const queryString = agentId ? `?agent_id=${agentId}` : '';
    const ret = await this.httpClient.get(`/acct/payment_user_payees${queryString}`);
    if (ret && ret.data && ret.data.data) {
      return UserPayee.fromJSON(ret.data.data);
    }
    return undefined;
  };

  public createUserPayee = async (formData: FormData): Promise<void> => {
    await this.httpClient.post('/acct/payment_user_payees', formData);
  };

  public updateUserPayee = async (id: string, formData: FormData): Promise<void> => {
    await this.httpClient.put(`/acct/payment_user_payees/${id}`, formData);
  };

  public getBankAccount = async (agentId: string): Promise<BankAccount | undefined> => {
    const queryString = agentId ? `?agent_id=${agentId}` : '';
    const ret = await this.httpClient.get(`/acct/payment_bank_accounts${queryString}`);
    if (ret && ret.data && ret.data.data) {
      return BankAccount.fromJSON(ret.data.data);
    }
    return undefined;
  };
  // agent_id
  public createBankAccount = async (formData: FormData): Promise<void> => {
    await this.httpClient.post('/acct/payment_bank_accounts', formData);
  };

  public updateBankAccount = async (id: string, formData: FormData): Promise<void> => {
    await this.httpClient.put(`/acct/payment_bank_accounts/${id}`, formData);
  };

  public getPaymentHistory = async (agentId: string): Promise<PaymentHistory> => {
    const queryString = agentId ? `?agent_id=${agentId}` : '';
    const ret = await this.httpClient.get(`/acct/payments/monthly_totals${queryString}`);
    return PaymentHistory.fromJSON(ret.data.data);
  };

  public getPaymentDetails = async (
    kaizenUserId: string,
    serviceMonth: Date,
    agentId?: string,
  ): Promise<PaymentDetail[]> => {
    kaizenUserId;
    const queryString = agentId ? `&agent_id=${agentId}` : '';
    const month = `${serviceMonth.getFullYear()}-${('00' + (serviceMonth.getMonth() + 1)).slice(-2)}-${(
      '00' + serviceMonth.getDate()
    ).slice(-2)}`;
    const ret = await this.httpClient.get(`/acct/optimizer_payments?service_month=${month}${queryString}`);
    return ret.data.data.map((d: any) => PaymentDetail.fromJSON(d));
  };

  public getPaymentExtProvider = async (kaizenUserId: string, agentId: string): Promise<PaymentExtProvider | null> => {
    const ret = await this.httpClient.get(`/acct/payment_user_ext_providers?kaizen_user_id=${agentId || kaizenUserId}`);
    if (ret && ret.data && ret.data.data) {
      return PaymentExtProvider.fromJSON(ret.data.data);
    } else {
      return null;
    }
  };
}

export default AcctWebApi;
